/**
 * @format
 */
import { Injectable } from "@angular/core";
import { BaseToolService } from "../../ng-editor/services/base-tool.service";
import { EditorActions } from "../../ng-editor/state/editor.actions";
import { Store } from "@ngrx/store";

export type KeyName = "ARROW_LEFT" | "ARROW_RIGHT" | "ARROW_UP" | "ARROW_DOWN";

export type KeyNameAndFunction = Record<KeyName, (keyCode: number) => boolean>;

@Injectable({
   providedIn: "root",
})
export class ContentEditableService extends BaseToolService {
   private _range: Range;

   constructor(private store: Store<any>) {
      super();
      this._range = null;
   }

   saveRange(selection: Selection): void {
      this._range = selection.rangeCount === 0 ? null : selection.getRangeAt(0);
   }

   restoreRange(): void {
      const selection = window.getSelection();
      selection.removeAllRanges();
      if (this._range) selection.addRange(this._range);
   }

   getRange(): Range {
      return this._range;
   }

   getSelectedNodeFromTarget(event: Event): Element | null {
      try {
         const selectedNode = <Element>event.target;

         if (Boolean(selectedNode.id)) {
            return selectedNode;
         }

         if (Boolean(selectedNode.attributes["ng-model"])) {
            selectedNode.id = selectedNode.attributes["ng-model"].value;
            return selectedNode;
         }

         const selectedParentNode = <Element>selectedNode.parentNode;
         if (Boolean(selectedParentNode.attributes["ng-model"])) {
            return selectedParentNode;
         }

         const selectedParentElement = <Element>selectedNode.parentElement;
         if (Boolean(selectedParentElement.attributes["ng-model"])) {
            return selectedParentElement;
         }

         const selectedParentElementId = <Element>selectedNode.parentElement;
         if (Boolean(selectedParentElementId.attributes["id"])) {
            return selectedParentElementId;
         }

         return null;
      } catch (e) {
         console.error("(Custom Error) GetSelectedNodeFromTarget", e);
      }
   }

   isValid(node: Element): boolean {
      try {
         return Boolean(node) && Boolean(node.id) && node.id !== "";
      } catch (e) {
         console.error("(Custom Error) Content Editable Service: isValid", e);
      }
   }

   cleanTemplateModelVariable(variable: string, templateModel: Record<string, string>): Record<string, string> {
      if (!Boolean(templateModel[variable])) {
         return templateModel;
      }

      const templateModelValue = templateModel[variable];
      templateModel[variable] = templateModelValue.replace(/&nbsp;/g, " ");
      return templateModel;
   }

   getRootContentEditableNode(node: Node): Node {
      if (!Boolean(node)) {
         return null;
      }

      if (Boolean(node) && node.nodeName !== "#text" && Boolean((<Element>node).attributes["ng-model"])) {
         return node;
      }

      return this.getRootContentEditableNode(node.parentNode);
   }

   getParentNode(node: Node): ParentNode & Node {
      if (Boolean(node.parentNode)) {
         return node.parentNode;
      }

      return null;
   }

   insertNodesInParentRange(parent: ParentNode & Node, nodeList: Node[], selection: Selection) {
      selection.anchorNode;
      const nextSiblingNodes = this.getAllNextSiblingNodesFromNode(selection.anchorNode);
      this.removeAllChildFromParent(parent, [selection.anchorNode, ...nextSiblingNodes]);
      nodeList.push(...nextSiblingNodes);
      this.appendAllChildren(parent, nodeList);
   }

   getNodesWithBrInBetween(range: Range, brNode: Node) {
      const value = range.startContainer.nodeValue;
      const part1 = value.slice(0, range.startOffset);
      const part2 = value.slice(range.startOffset, range.endOffset);
      const part3 = value.slice(range.endOffset);
      const auxContainer = document.createElement("span");

      auxContainer.append(part1);
      auxContainer.appendChild(brNode);
      auxContainer.append(part3);

      return Array.from(auxContainer.childNodes);
   }

   positioningCursor(selection: Selection, nodeToEvaluate: Node) {
      try {
         const newRange = new Range();
         const nodeIndex = this.findChildIndex(nodeToEvaluate.parentNode, nodeToEvaluate);
         newRange.setStart(nodeToEvaluate.parentNode, nodeIndex + 1);
         newRange.setEnd(nodeToEvaluate.parentNode, nodeIndex + 1);
         selection.removeAllRanges();
         selection.addRange(newRange);
      } catch (error) {
         console.error("(custom error) ", error.value);
      }
   }

   isText_(node: Node): boolean {
      return this.isText(node);
   }

   isSelectedNodeSMS(selectedNode: Element | null): boolean {
      if (!Boolean(selectedNode) || !Boolean(selectedNode.id)) {
         return false;
      }

      return selectedNode.id.indexOf("sms") !== -1;
   }

   dispatchWindowSelectionTimestamp(): void {
      this.store.dispatch(EditorActions.updateWindowSelectionTimestamp({ timestamp: new Date().getTime() }));
   }

   findChildIndex(parentNode: Node, wantedNode: Node): number {
      return Array.from(parentNode.childNodes).findIndex((value) => value === wantedNode);
   }

   validateTemplateOnBlur(modelName, updatedModel): any {
      let markAsWarning = false;
      let listOfModels = "";
      Object.entries(updatedModel).map((model) => {
         // checking on blur and when loading it from DB (previewtemplatetab or communication)
         if (model[0] === modelName || modelName === "") {
            const modelLowerCase = model[0].toLowerCase();
            let trimHtml: string = model[1] + "";
            if (model[0].endsWith("Sound")) {
               let rawDefaultContent: any = model[1];
               let matches = rawDefaultContent.match(/[^0-9]/g);
               if (matches !== null && rawDefaultContent.indexOf("template-context") !== -1) {
                  rawDefaultContent = rawDefaultContent.replace(
                     /(<template-context[^>]*>)([^<]*|\n)*?(<\/template-context>)/g,
                     ""
                  );
               }
               matches = matches !== null ? rawDefaultContent.match(/[a-zA-Z]/g) : null;
               markAsWarning = matches !== null;
               if (markAsWarning) {
                  listOfModels =
                     modelName !== "" ? modelName : listOfModels === "" ? model[0] : model[0] + ", " + listOfModels;
               }
            }

            if (modelLowerCase.indexOf("dateformat") !== -1) {
               markAsWarning = trimHtml.indexOf("&nbsp;") !== -1;
               if (markAsWarning) {
                  listOfModels =
                     modelName !== "" ? modelName : listOfModels === "" ? model[0] : model[0] + ", " + listOfModels;
               }
            }

            if (
               modelLowerCase.indexOf("color") !== -1 ||
               modelLowerCase.indexOf("logo") !== -1 ||
               modelLowerCase.indexOf("image") !== -1
            ) {
               if (window.navigator.userAgent.indexOf("safari") === -1 && trimHtml === "<br>") {
                  trimHtml = "";
               }

               markAsWarning =
                  trimHtml.indexOf("<br/>") !== -1 ||
                  trimHtml.indexOf("<br>") !== -1 ||
                  trimHtml.indexOf("&nbsp;") !== -1;
               if (markAsWarning) {
                  listOfModels =
                     modelName !== "" ? modelName : listOfModels === "" ? model[0] : model[0] + ", " + listOfModels;
               }
            }

            if (trimHtml !== undefined && trimHtml.indexOf("template-context") !== -1) {
               let matches = trimHtml.match(/(<span class="label2 label-info">)([^])*?(<\/span>)(<\/span>)/g);
               if (matches === null) {
                  matches = trimHtml.match(/(ng-version)([^<]*|\n)*?(")/g);
               }
               markAsWarning = matches !== null;
               if (markAsWarning) {
                  listOfModels =
                     modelName !== "" ? modelName : listOfModels === "" ? model[0] : model[0] + ", " + listOfModels;
               }
            }

            if (modelLowerCase.indexOf("emailsubject") !== -1 || modelLowerCase.indexOf("voice") !== -1) {
               let matches = trimHtml.match(/(<(?!\/?(template-context)(>|\s))[^<]+?>)/g);
               markAsWarning = matches !== null;
               if (markAsWarning) {
                  listOfModels =
                     modelName !== "" ? modelName : listOfModels === "" ? model[0] : model[0] + ", " + listOfModels;
               }
            }

            if (trimHtml.indexOf("<img") !== -1) {
               let matches = trimHtml.match(/(<(img)(>|\s)[^<\/]+?>)/g); // checking if img tag without close
               let matches2 = trimHtml.match(/(<(img)(>|\s)[^<]+?<\/img>)/g); //  checking if img tag with img close tag

               // checking if there is any image tag either not closed by itself or by </img> tag by checking if count is different
               if (matches !== null && matches2 !== null && matches.length !== matches2.length) {
                  markAsWarning = true;
               }

               if (markAsWarning) {
                  listOfModels =
                     modelName !== "" ? modelName : listOfModels === "" ? model[0] : model[0] + ", " + listOfModels;
               }
            }

            if (modelLowerCase.indexOf("emailfrom") !== -1) {
               markAsWarning = trimHtml === "" || trimHtml.indexOf("noreply@autoremind.us") === -1;
               if (markAsWarning) {
                  listOfModels =
                     modelName !== "" ? modelName : listOfModels === "" ? model[0] : model[0] + ", " + listOfModels;
               }
            }

            // implementing mark as warning for br with styles
            let brWithStyleMatch = trimHtml.match(/(<(br)(>|\s)[^<]+?>)/g);
            if (brWithStyleMatch !== null) {
               markAsWarning = true;
               if (markAsWarning) {
                  listOfModels =
                     modelName !== "" ? modelName : listOfModels === "" ? model[0] : model[0] + ", " + listOfModels;
               }
            }
         }
      });

      let response = { listOfModels, markAsWarning };
      return response;
   }

   removeSpanFromTemplateContext(html): string {
      let trimHtml = html;
      if (trimHtml !== null) {
         // this is required when the model contains only number (example 280)
         if (typeof trimHtml !== "string") {
            trimHtml = trimHtml.toString();
         }
         if (trimHtml !== undefined && trimHtml.indexOf("template-context") !== -1) {
            trimHtml = trimHtml.replaceAll(
               /(<span class="label2 label-info">)confirm keyword([^])*?(<\/span>)(<\/span>)/g,
               "$$$$$$confirm$$$$$$"
            );

            trimHtml = trimHtml
               .replaceAll(/(<span class="label2 label-info">)([^])*?(<\/span>)(<\/span>)/g, "") // remove added span from template
               .replaceAll(/(ng-version)([^<]*|\n)*?(")/g, ""); // remove added ng-version from template
         }
      }
      return trimHtml;
   }

   validateTemplateBeforeSave(tData): string {
      let trimHtml = tData[1] !== null ? tData[1].toString() : tData[1];
      if (trimHtml !== null) {
         // this is required when the model contains only number (example 280)
         if (typeof trimHtml !== "string") {
            trimHtml = trimHtml.toString();
         }

         trimHtml = this.correctIfAnyError(tData);
         trimHtml = this.removeSpanFromTemplateContext(trimHtml);
         trimHtml = trimHtml.replaceAll('contenteditable="false">', ">");
         trimHtml =
            tData[0].toLowerCase().indexOf("emailsubject") !== -1 ||
            tData[0].indexOf("voice") !== -1 ||
            tData[0].indexOf("sms") !== -1
               ? trimHtml.replace(/&nbsp;/g, " ")
               : trimHtml;
      }
      return trimHtml;
   }

   correctIfAnyError(model): string {
      let trimHtml = model[1].toString();
      const modelLowerCase = model[0].toLowerCase();
      if (modelLowerCase.indexOf("dateformat") !== -1 && trimHtml.indexOf("&nbsp;") !== -1) {
         trimHtml = trimHtml.replaceAll("&nbsp;", "");
      }

      // implementing auto correct for br with styles
      let brWithStyleMatch = trimHtml.match(/(<(br)(>|\s)[^<]+?>)/g);
      if (brWithStyleMatch !== null) {
         let i = 0;
         while (brWithStyleMatch !== null && i !== brWithStyleMatch.length) {
            trimHtml = trimHtml.replace(brWithStyleMatch[i], "<br>");
            i++;
         }
      }

      if (
         modelLowerCase.indexOf("color") !== -1 ||
         modelLowerCase.indexOf("logo") !== -1 ||
         modelLowerCase.indexOf("image") !== -1
      ) {
         if (trimHtml.indexOf("<br/>") !== -1 || trimHtml.indexOf("<br>") !== -1 || trimHtml.indexOf("&nbsp;") !== -1) {
            trimHtml = trimHtml.replaceAll("<br/>", "").replaceAll("<br>", "").replaceAll("&nbsp;", "");
         }
      }

      if (modelLowerCase.indexOf("emailsubject") !== -1) {
         trimHtml = trimHtml.replaceAll("<br/>", " ").replaceAll("<br>", " ").replaceAll("&nbsp;", " ");
      }

      // convert just in case its missed while copying or in raw
      if (trimHtml.indexOf("<br>") !== -1) {
         trimHtml = trimHtml.replaceAll("<br>", "<br/>");
      }

      if (modelLowerCase.indexOf("emailsubject") !== -1 || modelLowerCase.indexOf("voice") !== -1) {
         trimHtml = trimHtml.replaceAll(/(<(?!\/?(template-context)(>|\s))[^<]+?>)/g, "");
      }

      if (trimHtml.indexOf("<img") !== -1) {
         let matches2 = trimHtml.match(/(<(img)(>|\s)[^<]+?<\/img>)/g); //  checking if img tag with img close tag
         let matches3 = trimHtml.match(/(<(img)(>|\s)[^<]+?\/>)/g); //  checking if img tag with close tag

         // convert all img tag to not closed to close them correctly
         let i = 0;
         while (matches2 !== null && i !== matches2.length) {
            trimHtml = trimHtml.replace(matches2[i], matches2[i].replace("</img>", ""));
            i++;
         }
         i = 0;
         while (matches3 !== null && i !== matches3.length) {
            trimHtml = trimHtml.replace(matches3[i], matches3[i].replace("/>", ">"));
            i++;
         }
         i = 0;
         let matches = trimHtml.match(/(<(img)(>|\s)[^<\/]+?>)/g);
         let newHtml = trimHtml.replace(/(<(img)(>|\s)[^<\/]+?>)/g, "imagetag");
         while (i !== matches.length) {
            newHtml = newHtml.replace("imagetag", matches[i] + "</img>");
            i++;
         }
         trimHtml = newHtml;
      }

      if (modelLowerCase.indexOf("emailfrom") !== -1) {
         if (trimHtml === "" || trimHtml.indexOf("noreply@autoremind.us") === -1) {
            trimHtml =
               'noreply@autoremind.us (<template-context label="company name" th:remove="tag" th:text="${account.companyName}"></template-context>)';
         }
      }

      return trimHtml;
   }

   getTemplateVariableName(element: Element): string {
      return element.id.split(".")[1];
   }

   getInnerHtmlFormElement(element: Element): string {
      return document.getElementById(element.id).innerHTML;
   }

   isEmailMessageParameter(parameter: string): boolean{
     const parameterLowerCase = parameter.toLocaleLowerCase();
     return  (parameterLowerCase.includes('message') && parameterLowerCase.includes('email'));
   }
}
